import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';
import { getStudentCode } from '../../utils/studentCode';
import { exportPaymentReport, exportPaymentReportExcel } from '../../utils/exportUtils';
import './Reports.css';
import './Payments.css';
import './ReportPayments.css';

const NO_COHORT = '__geen_cohort__';
const LOCATION_LABELS = { paramaribo: 'Paramaribo', nickerie: 'Nickerie' };

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Alles' },
  { value: 'week', label: 'Afgelopen 7 dagen' },
  { value: 'month', label: 'Deze maand' },
  { value: 'quarter', label: 'Dit kwartaal' },
  { value: 'year', label: 'Dit jaar' },
  { value: 'custom', label: 'Aangepast datumbereik' }
];

const STATUS_OPTIONS = ['pending', 'partial', 'paid', 'overdue', 'cancelled'];

const toId = (value) => String(value?.id || value?._id || value || '');
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('nl-NL') : '-');
const totalOf = (payment) => Number((payment.totalAmount ?? payment.amount) || 0);
const paidOf = (payment) => Number(payment.paidAmount || 0);
const remainingOf = (payment) => Math.max(0, totalOf(payment) - paidOf(payment));

const ReportPayments = () => {
  const { t, i18n } = useTranslation();
  const { selectedLocation } = useOutletContext();
  const { user } = useAuth();
  const { data } = useData();

  const isManager = user?.role === 'manager';
  const [locationFilter, setLocationFilter] = useState(() => (
    isManager ? String(user?.location || '').toLowerCase() : String(selectedLocation || 'all').toLowerCase()
  ));
  const [studyFilter, setStudyFilter] = useState('');
  const [cohortFilter, setCohortFilter] = useState('');
  const [studentFilter, setStudentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [schoolYearFilter, setSchoolYearFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [drawerStudentId, setDrawerStudentId] = useState(null);

  const formatCurrency = (amount) => {
    const locale = i18n.language === 'nl' ? 'nl-NL' : 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'SRD' }).format(Number(amount || 0));
  };

  const isWithinPeriod = (value) => {
    if (periodFilter === 'all') return true;
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (periodFilter === 'week') {
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfToday.getDate() - 6);
      return date >= startOfWeek;
    }
    if (periodFilter === 'month') return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
    if (periodFilter === 'quarter') return date >= new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    if (periodFilter === 'year') return date.getFullYear() === now.getFullYear();
    if (periodFilter === 'custom') {
      const from = customDateFrom ? new Date(customDateFrom) : null;
      const to = customDateTo ? new Date(`${customDateTo}T23:59:59`) : null;
      if (from && date < from) return false;
      if (to && date > to) return false;
      return true;
    }
    return true;
  };

  const statusLabel = (status) => t(`payment.status.${status}`, status || '-');
  const paymentTypeLabel = (payment) => {
    const key = payment.paymentTypeKey || payment.paymentType;
    return t(`payment.${key}`, key || '-');
  };

  const studentsById = useMemo(() => {
    const map = new Map();
    (data.students || []).forEach((student) => map.set(toId(student), student));
    return map;
  }, [data.students]);

  const cohortsById = useMemo(() => {
    const map = new Map();
    (data.cohorts || []).forEach((cohort) => map.set(toId(cohort), cohort));
    return map;
  }, [data.cohorts]);

  // Studie per student: via de studies van het cohort, aangevuld met de studies van de vakinschrijvingen.
  const studiesByStudent = useMemo(() => {
    const courseToStudy = new Map();
    (data.studies || []).forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        (year.modules || []).forEach((module) => {
          (module.courses || []).forEach((course) => courseToStudy.set(toId(course), toId(study)));
        });
      });
    });

    const map = new Map();
    const add = (studentId, studyId) => {
      if (!studentId || !studyId) return;
      if (!map.has(studentId)) map.set(studentId, new Set());
      map.get(studentId).add(studyId);
    };

    (data.students || []).forEach((student) => {
      const cohort = cohortsById.get(toId(student.cohort));
      (cohort?.studies || []).forEach((study) => add(toId(student), toId(study)));
    });
    (data.enrollments || []).forEach((enrollment) => {
      if (enrollment.status === 'cancelled') return;
      const enrollmentStudy = enrollment.studyId || enrollment.study;
      add(toId(enrollment.studentId), enrollmentStudy ? toId(enrollmentStudy) : courseToStudy.get(toId(enrollment.courseId)));
    });
    return map;
  }, [cohortsById, data.enrollments, data.students, data.studies]);

  const studyNameById = useMemo(() => {
    const map = new Map();
    (data.studies || []).forEach((study) => map.set(toId(study), study.name));
    return map;
  }, [data.studies]);

  // Verrijkte betalingsregels met student, cohort en studie.
  const rows = useMemo(() => (data.payments || []).map((payment) => {
    const studentId = toId(payment.studentId || payment.student);
    const student = studentsById.get(studentId);
    const cohortId = toId(student?.cohort) || NO_COHORT;
    const cohort = cohortsById.get(cohortId) || student?.cohort || null;
    const studyIds = Array.from(studiesByStudent.get(studentId) || []);
    const installments = [...(payment.payments || [])].sort((a, b) => new Date(a.paymentDate || 0) - new Date(b.paymentDate || 0));
    return {
      payment,
      studentId,
      studentName: payment.student?.name || student?.name || '-',
      studentCode: payment.student?.studentCode || getStudentCode(student),
      email: payment.student?.email || student?.email || '',
      location: String(payment.student?.location || student?.location || '').toLowerCase(),
      cohortId,
      cohortName: cohortId === NO_COHORT ? 'Zonder cohort' : (cohort?.name || cohort?.code || 'Onbekend cohort'),
      studyIds,
      studyNames: studyIds.map((id) => studyNameById.get(id)).filter(Boolean),
      schoolYear: payment.schoolYearLabel || payment.schoolYear || '',
      total: totalOf(payment),
      paid: paidOf(payment),
      remaining: remainingOf(payment),
      installments,
      lastInstallment: installments[installments.length - 1] || null
    };
  }), [cohortsById, data.payments, studentsById, studiesByStudent, studyNameById]);

  const schoolYears = useMemo(
    () => Array.from(new Set(rows.map((row) => row.schoolYear).filter(Boolean))).sort(),
    [rows]
  );

  const cohortOptions = useMemo(() => (data.cohorts || [])
    .filter((cohort) => locationFilter === 'all' || String(cohort.location || '').toLowerCase() === locationFilter)
    .filter((cohort) => !studyFilter || (cohort.studies || []).some((study) => toId(study) === studyFilter))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl', { numeric: true })), [data.cohorts, locationFilter, studyFilter]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (locationFilter !== 'all' && row.location !== locationFilter) return false;
      if (studyFilter && !row.studyIds.includes(studyFilter)) return false;
      if (cohortFilter && row.cohortId !== cohortFilter) return false;
      if (studentFilter && row.studentId !== studentFilter) return false;
      if (statusFilter && row.payment.status !== statusFilter) return false;
      if (schoolYearFilter && row.schoolYear !== schoolYearFilter) return false;
      if (periodFilter !== 'all') {
        const dates = [row.payment.paymentDate, row.payment.dueDate, row.payment.createdAt, ...row.installments.map((item) => item.paymentDate)];
        if (!dates.some((value) => isWithinPeriod(value))) return false;
      }
      if (query && !`${row.studentName} ${row.studentCode} ${row.email} ${row.cohortName} ${row.studyNames.join(' ')}`.toLowerCase().includes(query)) return false;
      return true;
    }).sort((a, b) => a.studentName.localeCompare(b.studentName, 'nl'));
  }, [rows, locationFilter, studyFilter, cohortFilter, studentFilter, statusFilter, schoolYearFilter, periodFilter, customDateFrom, customDateTo, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const studentOptions = useMemo(() => {
    const map = new Map();
    rows
      .filter((row) => locationFilter === 'all' || row.location === locationFilter)
      .filter((row) => !studyFilter || row.studyIds.includes(studyFilter))
      .filter((row) => !cohortFilter || row.cohortId === cohortFilter)
      .forEach((row) => map.set(row.studentId, row.studentName));
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], 'nl'));
  }, [rows, locationFilter, studyFilter, cohortFilter]);

  const totals = useMemo(() => filteredRows.reduce((acc, row) => ({
    total: acc.total + row.total,
    paid: acc.paid + row.paid,
    remaining: acc.remaining + row.remaining,
    installments: acc.installments + row.installments.length
  }), { total: 0, paid: 0, remaining: 0, installments: 0 }), [filteredRows]);

  const studentCount = useMemo(() => new Set(filteredRows.map((row) => row.studentId)).size, [filteredRows]);

  const cohortSummary = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((row) => {
      if (!map.has(row.cohortId)) {
        map.set(row.cohortId, {
          cohortId: row.cohortId,
          cohortName: row.cohortName,
          location: row.location,
          students: new Set(),
          total: 0,
          paid: 0,
          remaining: 0
        });
      }
      const entry = map.get(row.cohortId);
      entry.students.add(row.studentId);
      entry.total += row.total;
      entry.paid += row.paid;
      entry.remaining += row.remaining;
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.cohortId === NO_COHORT) return 1;
      if (b.cohortId === NO_COHORT) return -1;
      return a.cohortName.localeCompare(b.cohortName, 'nl', { numeric: true });
    });
  }, [filteredRows]);

  const percent = (paid, total) => (total > 0 ? Math.round((paid / total) * 100) : 0);

  const resetFilters = () => {
    if (!isManager) setLocationFilter('all');
    setStudyFilter('');
    setCohortFilter('');
    setStudentFilter('');
    setStatusFilter('');
    setSchoolYearFilter('');
    setPeriodFilter('all');
    setCustomDateFrom('');
    setCustomDateTo('');
    setSearch('');
  };

  const activeFilterText = [
    locationFilter !== 'all' && `Locatie: ${LOCATION_LABELS[locationFilter] || locationFilter}`,
    studyFilter && `Studie: ${studyNameById.get(studyFilter) || ''}`,
    cohortFilter && `Cohort: ${cohortFilter === NO_COHORT ? 'Zonder cohort' : (cohortsById.get(cohortFilter)?.name || '')}`,
    studentFilter && `Student: ${studentOptions.find(([id]) => id === studentFilter)?.[1] || ''}`,
    statusFilter && `Status: ${statusLabel(statusFilter)}`,
    schoolYearFilter && `Schooljaar: ${schoolYearFilter}`,
    periodFilter !== 'all' && `Periode: ${PERIOD_OPTIONS.find((o) => o.value === periodFilter)?.label}`
  ].filter(Boolean).join(' · ') || 'Geen filters';

  const fileStamp = new Date().toISOString().slice(0, 10);

  // Exportstructuur: cohort -> student -> betalingen (met aflossingen), met totalen per niveau.
  const buildExportGroups = () => {
    const cohorts = new Map();
    filteredRows.forEach((row) => {
      if (!cohorts.has(row.cohortId)) {
        cohorts.set(row.cohortId, { cohortId: row.cohortId, cohortName: row.cohortName, students: new Map(), total: 0, paid: 0, remaining: 0 });
      }
      const cohort = cohorts.get(row.cohortId);
      if (!cohort.students.has(row.studentId)) {
        cohort.students.set(row.studentId, {
          studentName: row.studentName,
          email: row.email,
          studyNames: row.studyNames,
          rows: [],
          total: 0,
          paid: 0,
          remaining: 0,
          installmentCount: 0
        });
      }
      const student = cohort.students.get(row.studentId);
      student.rows.push(row);
      student.total += row.total;
      student.paid += row.paid;
      student.remaining += row.remaining;
      student.installmentCount += row.installments.length;
      cohort.total += row.total;
      cohort.paid += row.paid;
      cohort.remaining += row.remaining;
    });

    return Array.from(cohorts.values())
      .sort((a, b) => {
        if (a.cohortId === NO_COHORT) return 1;
        if (b.cohortId === NO_COHORT) return -1;
        return a.cohortName.localeCompare(b.cohortName, 'nl', { numeric: true });
      })
      .map((cohort) => ({
        ...cohort,
        students: Array.from(cohort.students.values()).sort((a, b) => a.studentName.localeCompare(b.studentName, 'nl'))
      }));
  };

  const exportExcel = () => {
    const groups = buildExportGroups();
    const workbook = XLSX.utils.book_new();

    const summarySheet = XLSX.utils.json_to_sheet([
      ...cohortSummary.map((entry) => ({
        Cohort: entry.cohortName,
        Locatie: LOCATION_LABELS[entry.location] || entry.location,
        Studenten: entry.students.size,
        'Totaal (SRD)': entry.total,
        'Betaald (SRD)': entry.paid,
        'Openstaand (SRD)': entry.remaining,
        '% betaald': percent(entry.paid, entry.total)
      })),
      {
        Cohort: 'TOTAAL',
        Locatie: '',
        Studenten: studentCount,
        'Totaal (SRD)': totals.total,
        'Betaald (SRD)': totals.paid,
        'Openstaand (SRD)': totals.remaining,
        '% betaald': percent(totals.paid, totals.total)
      }
    ]);
    summarySheet['!cols'] = [{ wch: 28 }, { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 16 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Per cohort');

    // Detailblad: per cohort per student de betalingen, aflossingen en totalen.
    const header = ['Cohort', 'Student', 'Regel', 'Schooljaar / Type', 'Datum', 'Methode', 'Notitie', 'Totaal (SRD)', 'Betaald (SRD)', 'Openstaand (SRD)', 'Status'];
    const aoa = [
      ['Betalingsrapport NLBA'],
      [`Afgedrukt op ${new Date().toLocaleDateString('nl-NL')}`, '', activeFilterText],
      [],
      header
    ];
    groups.forEach((cohort) => {
      aoa.push([`Cohort: ${cohort.cohortName}`]);
      cohort.students.forEach((student) => {
        student.rows.forEach((row) => {
          aoa.push([
            cohort.cohortName,
            student.studentName,
            'Betaling',
            `${row.schoolYear || '-'} · ${paymentTypeLabel(row.payment)}`,
            row.payment.dueDate ? `Vervalt ${formatDate(row.payment.dueDate)}` : '',
            row.payment.paymentMethod || '',
            row.payment.description || '',
            row.total,
            row.paid,
            row.remaining,
            statusLabel(row.payment.status)
          ]);
          let running = 0;
          row.installments.forEach((item, index) => {
            running += Number(item.amount || 0);
            aoa.push([
              cohort.cohortName,
              student.studentName,
              `  Aflossing ${index + 1}`,
              row.schoolYear || '',
              formatDate(item.paymentDate),
              item.paymentMethod || '',
              item.notes || '',
              '',
              Number(item.amount || 0),
              Math.max(0, row.total - running),
              ''
            ]);
          });
        });
        aoa.push(['', `Totaal ${student.studentName}`, `${student.installmentCount} aflossing(en)`, '', '', '', '', student.total, student.paid, student.remaining, `${percent(student.paid, student.total)}% betaald`]);
        aoa.push([]);
      });
      aoa.push([`Totaal cohort ${cohort.cohortName}`, `${cohort.students.length} studenten`, '', '', '', '', '', cohort.total, cohort.paid, cohort.remaining, `${percent(cohort.paid, cohort.total)}% betaald`]);
      aoa.push([]);
    });
    aoa.push(['TOTAAL', `${studentCount} studenten`, '', '', '', '', '', totals.total, totals.paid, totals.remaining, `${percent(totals.paid, totals.total)}% betaald`]);

    const detailSheet = XLSX.utils.aoa_to_sheet(aoa);
    detailSheet['!cols'] = [{ wch: 22 }, { wch: 28 }, { wch: 16 }, { wch: 26 }, { wch: 16 }, { wch: 14 }, { wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(workbook, detailSheet, 'Per student');

    // Plat overzicht van alle aflossingen (handig om te filteren in Excel).
    const installmentRows = [];
    filteredRows.forEach((row) => {
      let running = 0;
      row.installments.forEach((item, index) => {
        running += Number(item.amount || 0);
        installmentRows.push({
          Cohort: row.cohortName,
          Student: row.studentName,
          Schooljaar: row.schoolYear,
          Nr: index + 1,
          Datum: formatDate(item.paymentDate),
          'Bedrag (SRD)': Number(item.amount || 0),
          Methode: item.paymentMethod || '',
          Notitie: item.notes || '',
          'Resterend na aflossing (SRD)': Math.max(0, row.total - running)
        });
      });
    });
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(installmentRows.length ? installmentRows : [{ Melding: 'Geen aflossingen' }]), 'Aflossingen');

    XLSX.writeFile(workbook, `Betalingsrapport_${fileStamp}.xlsx`);
  };

  const exportPDF = () => {
    const groups = buildExportGroups();
    const doc = new jsPDF({ orientation: 'landscape' });
    const purple = [107, 29, 110];
    const lightPurple = [243, 232, 244];
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.text('Betalingsrapport NLBA', 14, 16);
    doc.setFontSize(9);
    doc.text(`Afgedrukt op ${new Date().toLocaleDateString('nl-NL')} · ${activeFilterText}`, 14, 22);
    doc.text(
      `Studenten: ${studentCount} · Totaal: ${formatCurrency(totals.total)} · Betaald: ${formatCurrency(totals.paid)} · Openstaand: ${formatCurrency(totals.remaining)} (${percent(totals.paid, totals.total)}% betaald)`,
      14,
      27
    );

    doc.autoTable({
      startY: 32,
      head: [['Cohort', 'Locatie', 'Studenten', 'Totaal', 'Betaald', 'Openstaand', '% betaald']],
      body: [
        ...cohortSummary.map((entry) => [
          entry.cohortName,
          LOCATION_LABELS[entry.location] || entry.location,
          entry.students.size,
          formatCurrency(entry.total),
          formatCurrency(entry.paid),
          formatCurrency(entry.remaining),
          `${percent(entry.paid, entry.total)}%`
        ]),
        ['TOTAAL', '', studentCount, formatCurrency(totals.total), formatCurrency(totals.paid), formatCurrency(totals.remaining), `${percent(totals.paid, totals.total)}%`].map((content) => ({ content, styles: { fontStyle: 'bold', fillColor: lightPurple } }))
      ],
      styles: { fontSize: 8 },
      headStyles: { fillColor: purple }
    });

    const bold = (content, extra = {}) => ({ content, styles: { fontStyle: 'bold', ...extra } });
    const muted = (content) => ({ content, styles: { textColor: [90, 90, 90] } });

    groups.forEach((cohort) => {
      let startY = doc.lastAutoTable.finalY + 10;
      if (startY > pageHeight - 40) {
        doc.addPage();
        startY = 16;
      }
      doc.setFontSize(12);
      doc.setTextColor(...purple);
      doc.text(`Cohort: ${cohort.cohortName}`, 14, startY);
      doc.setTextColor(0, 0, 0);

      const body = [];
      cohort.students.forEach((student) => {
        body.push([
          bold(student.studentName, { fillColor: [250, 245, 251] }),
          { content: student.studyNames.join(', ') || '', colSpan: 8, styles: { fillColor: [250, 245, 251], textColor: [90, 90, 90] } }
        ]);
        student.rows.forEach((row) => {
          body.push([
            'Betaling',
            `${row.schoolYear || '-'} · ${paymentTypeLabel(row.payment)}`,
            row.payment.dueDate ? `vervalt ${formatDate(row.payment.dueDate)}` : '-',
            row.payment.paymentMethod || '-',
            formatCurrency(row.total),
            formatCurrency(row.paid),
            formatCurrency(row.remaining),
            statusLabel(row.payment.status),
            row.payment.description || ''
          ]);
          let running = 0;
          row.installments.forEach((item, index) => {
            running += Number(item.amount || 0);
            body.push([
              muted(`   Aflossing ${index + 1}`),
              muted(row.schoolYear || ''),
              muted(formatDate(item.paymentDate)),
              muted(item.paymentMethod || '-'),
              muted(''),
              muted(formatCurrency(item.amount)),
              muted(formatCurrency(Math.max(0, row.total - running))),
              muted(''),
              muted(item.notes || '')
            ]);
          });
        });
        body.push([
          bold(`Totaal ${student.studentName}`),
          bold(`${student.installmentCount} aflossing(en)`),
          bold(''),
          bold(''),
          bold(formatCurrency(student.total)),
          bold(formatCurrency(student.paid)),
          bold(formatCurrency(student.remaining)),
          bold(`${percent(student.paid, student.total)}% betaald`),
          bold('')
        ]);
      });
      body.push([
        `Totaal cohort ${cohort.cohortName}`,
        `${cohort.students.length} studenten`,
        '',
        '',
        formatCurrency(cohort.total),
        formatCurrency(cohort.paid),
        formatCurrency(cohort.remaining),
        `${percent(cohort.paid, cohort.total)}% betaald`,
        ''
      ].map((content) => bold(content, { fillColor: lightPurple })));

      doc.autoTable({
        startY: startY + 3,
        head: [['Regel', 'Schooljaar / Type', 'Datum', 'Methode', 'Totaal', 'Betaald', 'Openstaand', 'Status', 'Notitie']],
        body,
        styles: { fontSize: 7.5, cellPadding: 1.6 },
        headStyles: { fillColor: purple },
        columnStyles: {
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' },
          8: { cellWidth: 50 }
        }
      });
    });

    let endY = doc.lastAutoTable.finalY + 8;
    if (endY > pageHeight - 20) {
      doc.addPage();
      endY = 16;
    }
    doc.autoTable({
      startY: endY,
      body: [[
        'TOTAAL ALLE COHORTEN',
        `${studentCount} studenten`,
        `Totaal ${formatCurrency(totals.total)}`,
        `Betaald ${formatCurrency(totals.paid)}`,
        `Openstaand ${formatCurrency(totals.remaining)}`,
        `${percent(totals.paid, totals.total)}% betaald`
      ].map((content) => bold(content, { fillColor: purple, textColor: [255, 255, 255] }))],
      styles: { fontSize: 8.5 }
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text(`Pagina ${page} van ${pageCount}`, doc.internal.pageSize.getWidth() - 30, pageHeight - 6);
    }

    doc.save(`Betalingsrapport_${fileStamp}.pdf`);
  };

  const drawerRows = drawerStudentId ? rows.filter((row) => row.studentId === drawerStudentId) : [];
  const drawerStudent = drawerStudentId ? studentsById.get(drawerStudentId) : null;
  const drawerTotals = drawerRows.reduce((acc, row) => ({
    total: acc.total + row.total, paid: acc.paid + row.paid, remaining: acc.remaining + row.remaining
  }), { total: 0, paid: 0, remaining: 0 });

  const exportStudentStatement = (format) => {
    if (!drawerStudent) return;
    const payments = drawerRows.map((row) => row.payment);
    const studyYears = (data.studies || []).flatMap((study) => study.studyYears || []);
    const fileName = `payment_statement_${String(drawerStudent.name || 'student').replace(/\s+/g, '_')}`;
    if (format === 'pdf') exportPaymentReport(payments, drawerStudent, studyYears, fileName);
    else exportPaymentReportExcel(payments, drawerStudent, studyYears, fileName);
  };

  return (
    <div className="reports-page report-payments-page">
      <div className="rp-header">
        <div>
          <h1>{t('report.paymentReport', 'Betalingen Rapport')}</h1>
          <p className="rp-muted">Overzicht van alle betalingen en aflossingen, met totalen per cohort. Klik op een student om alle aflossingen te zien.</p>
        </div>
        <div className="rp-header-actions">
          <button type="button" className="btn-secondary" onClick={exportPDF} disabled={!filteredRows.length}>{t('report.exportPDF')}</button>
          <button type="button" className="btn-primary" onClick={exportExcel} disabled={!filteredRows.length}>{t('report.exportExcel')}</button>
        </div>
      </div>

      <div className="rp-filters">
        {!isManager && (
          <label>
            <span>Locatie</span>
            <select value={locationFilter} onChange={(e) => { setLocationFilter(e.target.value); setCohortFilter(''); setStudentFilter(''); }}>
              <option value="all">Alle locaties</option>
              <option value="paramaribo">Paramaribo</option>
              <option value="nickerie">Nickerie</option>
            </select>
          </label>
        )}
        <label>
          <span>Studie</span>
          <select value={studyFilter} onChange={(e) => { setStudyFilter(e.target.value); setCohortFilter(''); setStudentFilter(''); }}>
            <option value="">Alle studies</option>
            {[...(data.studies || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl')).map((study) => (
              <option key={toId(study)} value={toId(study)}>{study.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Cohort</span>
          <select value={cohortFilter} onChange={(e) => { setCohortFilter(e.target.value); setStudentFilter(''); }}>
            <option value="">Alle cohorten</option>
            {cohortOptions.map((cohort) => (
              <option key={toId(cohort)} value={toId(cohort)}>{cohort.name || cohort.code}</option>
            ))}
            <option value={NO_COHORT}>Zonder cohort</option>
          </select>
        </label>
        <label>
          <span>Student</span>
          <select value={studentFilter} onChange={(e) => setStudentFilter(e.target.value)}>
            <option value="">Alle studenten ({studentOptions.length})</option>
            {studentOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Alle statussen</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{statusLabel(status)}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Schooljaar</span>
          <select value={schoolYearFilter} onChange={(e) => setSchoolYearFilter(e.target.value)}>
            <option value="">Alle schooljaren</option>
            {schoolYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Periode</span>
          <select value={periodFilter} onChange={(e) => { setPeriodFilter(e.target.value); setCustomDateFrom(''); setCustomDateTo(''); }}>
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        {periodFilter === 'custom' && (
          <>
            <label>
              <span>Van</span>
              <input type="date" value={customDateFrom} onChange={(e) => setCustomDateFrom(e.target.value)} />
            </label>
            <label>
              <span>Tot</span>
              <input type="date" value={customDateTo} min={customDateFrom || undefined} onChange={(e) => setCustomDateTo(e.target.value)} />
            </label>
          </>
        )}
        <label className="rp-search">
          <span>Zoeken</span>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Naam, studentcode, e-mail, cohort of studie…" />
        </label>
        <div className="rp-filter-reset">
          <button type="button" className="btn-secondary" onClick={resetFilters}>Reset</button>
        </div>
      </div>

      <div className="rp-stats">
        <div className="rp-stat"><span>Studenten</span><strong>{studentCount}</strong></div>
        <div className="rp-stat"><span>Totaal te betalen</span><strong>{formatCurrency(totals.total)}</strong></div>
        <div className="rp-stat"><span>Totaal betaald</span><strong className="paid">{formatCurrency(totals.paid)}</strong></div>
        <div className="rp-stat"><span>Openstaand</span><strong className="pending">{formatCurrency(totals.remaining)}</strong></div>
        <div className="rp-stat">
          <span>Betaald</span>
          <strong>{percent(totals.paid, totals.total)}%</strong>
          <div className="installment-progress"><div className="installment-progress-bar" style={{ width: `${percent(totals.paid, totals.total)}%` }} /></div>
        </div>
      </div>

      <section className="rp-section">
        <h2>Totalen per cohort</h2>
        <div className="rp-table-wrap">
          <table className="rp-table">
            <thead>
              <tr>
                <th>Cohort</th>
                <th>Locatie</th>
                <th className="num">Studenten</th>
                <th className="num">Totaal</th>
                <th className="num">Betaald</th>
                <th className="num">Openstaand</th>
                <th>% betaald</th>
              </tr>
            </thead>
            <tbody>
              {cohortSummary.length === 0 && (
                <tr><td colSpan={7} className="rp-empty">Geen betalingen gevonden voor deze filters.</td></tr>
              )}
              {cohortSummary.map((entry) => (
                <tr key={entry.cohortId} className="rp-clickable" onClick={() => setCohortFilter(entry.cohortId)} title="Filter op dit cohort">
                  <td><strong>{entry.cohortName}</strong></td>
                  <td>{LOCATION_LABELS[entry.location] || entry.location || '-'}</td>
                  <td className="num">{entry.students.size}</td>
                  <td className="num">{formatCurrency(entry.total)}</td>
                  <td className="num paid">{formatCurrency(entry.paid)}</td>
                  <td className="num pending">{formatCurrency(entry.remaining)}</td>
                  <td>
                    <div className="rp-percent">
                      <div className="installment-progress"><div className="installment-progress-bar" style={{ width: `${percent(entry.paid, entry.total)}%` }} /></div>
                      <span>{percent(entry.paid, entry.total)}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {cohortSummary.length > 0 && (
              <tfoot>
                <tr>
                  <td>Totaal</td>
                  <td />
                  <td className="num">{studentCount}</td>
                  <td className="num">{formatCurrency(totals.total)}</td>
                  <td className="num paid">{formatCurrency(totals.paid)}</td>
                  <td className="num pending">{formatCurrency(totals.remaining)}</td>
                  <td>{percent(totals.paid, totals.total)}%</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      <section className="rp-section">
        <h2>Betalingen ({filteredRows.length})</h2>
        <div className="rp-table-wrap">
          <table className="rp-table rp-detail-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Cohort</th>
                <th>Studie</th>
                <th>Schooljaar</th>
                <th>Type</th>
                <th className="num">Totaal</th>
                <th className="num">Betaald</th>
                <th className="num">Openstaand</th>
                <th className="num">Afl.</th>
                <th>Laatste aflossing</th>
                <th>Methode</th>
                <th>Vervaldatum</th>
                <th>Status</th>
                <th className="rp-actions-col" />
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && (
                <tr><td colSpan={14} className="rp-empty">Geen betalingen gevonden voor deze filters.</td></tr>
              )}
              {filteredRows.map((row) => (
                <tr key={row.payment.id}>
                  <td>
                    <button type="button" className="rp-student-link" onClick={() => setDrawerStudentId(row.studentId)}>{row.studentName}</button>
                    <div className="rp-muted">{[row.studentCode, LOCATION_LABELS[row.location] || row.location].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td>{row.cohortName}</td>
                  <td>{row.studyNames.join(', ') || '-'}</td>
                  <td>{row.schoolYear || '-'}</td>
                  <td>{paymentTypeLabel(row.payment)}</td>
                  <td className="num">{formatCurrency(row.total)}</td>
                  <td className="num paid">{formatCurrency(row.paid)}</td>
                  <td className="num pending">{formatCurrency(row.remaining)}</td>
                  <td className="num">{row.installments.length}</td>
                  <td>{row.lastInstallment ? `${formatDate(row.lastInstallment.paymentDate)} · ${formatCurrency(row.lastInstallment.amount)}` : '-'}</td>
                  <td>{row.payment.paymentMethod || '-'}</td>
                  <td>{formatDate(row.payment.dueDate)}</td>
                  <td><span className={`status-badge status-${row.payment.status}`}>{statusLabel(row.payment.status)}</span></td>
                  <td className="rp-actions-col">
                    <button type="button" className="btn-small" onClick={() => setDrawerStudentId(row.studentId)}>Aflossingen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        isOpen={!!drawerStudentId}
        onClose={() => setDrawerStudentId(null)}
        title={`Aflossingen – ${drawerRows[0]?.studentName || drawerStudent?.name || ''}`}
        position="right"
      >
        {drawerStudentId && (
          <div className="installment-drawer">
            <div className="installment-drawer-summary">
              <div><span>Totaal</span><strong>{formatCurrency(drawerTotals.total)}</strong></div>
              <div><span>Betaald</span><strong className="paid">{formatCurrency(drawerTotals.paid)}</strong></div>
              <div><span>Openstaand</span><strong className="pending">{formatCurrency(drawerTotals.remaining)}</strong></div>
            </div>
            <dl className="installment-facts">
              <div><dt>E-mail</dt><dd>{drawerRows[0]?.email || '-'}</dd></div>
              <div><dt>Locatie</dt><dd>{LOCATION_LABELS[drawerRows[0]?.location] || '-'}</dd></div>
              <div><dt>Cohort</dt><dd>{drawerRows[0]?.cohortName || '-'}</dd></div>
              <div><dt>Studie</dt><dd>{drawerRows[0]?.studyNames.join(', ') || '-'}</dd></div>
            </dl>

            {drawerRows.map((row) => {
              let running = 0;
              return (
                <div key={row.payment.id} className="rp-drawer-payment">
                  <div className="rp-drawer-payment-head">
                    <div>
                      <strong>{row.schoolYear || paymentTypeLabel(row.payment)}</strong>
                      <div className="installment-muted">
                        {paymentTypeLabel(row.payment)} · vervaldatum {formatDate(row.payment.dueDate)} · {row.payment.paymentMethod || 'methode onbekend'}
                      </div>
                    </div>
                    <span className={`status-badge status-${row.payment.status}`}>{statusLabel(row.payment.status)}</span>
                  </div>
                  <div className="installment-muted">
                    Totaal {formatCurrency(row.total)} · betaald {formatCurrency(row.paid)} · openstaand {formatCurrency(row.remaining)}
                  </div>
                  {row.installments.length === 0 ? (
                    <p className="installments-empty">Nog geen aflossingen geboekt.</p>
                  ) : (
                    <ol className="installment-timeline">
                      {row.installments.map((item, index) => {
                        running += Number(item.amount || 0);
                        return (
                          <li key={item.id || index}>
                            <div className="installment-timeline-head">
                              <strong>{formatCurrency(item.amount)}</strong>
                              <span>{formatDate(item.paymentDate)}</span>
                            </div>
                            <div className="installment-muted">
                              #{index + 1} · {item.paymentMethod || 'Methode onbekend'} · Resterend na deze aflossing: {formatCurrency(Math.max(0, row.total - running))}
                            </div>
                            {item.notes && <div className="installment-note">{item.notes}</div>}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              );
            })}

            <div className="installment-drawer-actions">
              <button type="button" className="btn-secondary" onClick={() => exportStudentStatement('pdf')} disabled={!drawerStudent}>Afschrift PDF</button>
              <button type="button" className="btn-secondary" onClick={() => exportStudentStatement('excel')} disabled={!drawerStudent}>Afschrift Excel</button>
              <button type="button" className="btn-small" onClick={() => setDrawerStudentId(null)}>Sluiten</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ReportPayments;
